// Benepack OMS · axios client
// JWT interceptor + 401 自动 refresh + error envelope → i18n message
// 后续 codegen 跑 `pnpm codegen:openapi` 生成 src/api/generated/ · 该层会 import 本 client
//
// 2026-05-16: axios 1.x types 跟 moduleResolution=Bundler 兼容性问题 (反复实测 · 含 1.7.9 + 1.16.1):
//   - default + named 单一 statement: TS 把 axios 解析为 namespace · .create 找不到
//   - default + named type 分离: 同样 TS2305 (named export 不在 module 顶层)
//   - 降版本 1.16.1 → 1.7.9: 同样不行 (UMD namespace 范式本身的问题)
// 根因: axios 1.x types 用 `declare namespace axios + class/interface` UMD 范式 ·
//   named import 不工作 · 但 default export 在 runtime 真有 .create
// 修法: 用 axios runtime 强制 cast + ad-hoc 最小类型 (业务足够 · 不深度 typing)
// 配套 backlog (Phase 2 D-4+): 切 native fetch + 自写 wrapper · 或 ky / wretch (无 axios types 坑)
import axiosRaw from 'axios';
import { message } from 'antd';
import i18n from '@/i18n';

// ===== ad-hoc 最小类型 (足够业务 · 见上注释) =====
// request/post/get/put/delete 第 3 参 (config) 用 InternalAxiosRequestConfig
// 让 _skipAuth / _retried 等业务字段可直接传入 · 不需 cast Record<string, unknown>
interface AxiosInstance {
  request<T = unknown>(config: InternalAxiosRequestConfig): Promise<{ data: T }>;
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig,
  ): Promise<{ data: T }>;
  // v0.1-frontend-rc9 · WP-07 · trade shipment/RN 状态机动作走 PATCH (gateway v7.7.0 路由是 PATCH ·
  // 区别于 quotation/SO/PO 的 POST · 见 trade_handler.go:99-112) · axios runtime 真有 .patch
  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig,
  ): Promise<{ data: T }>;
  get<T = unknown>(url: string, config?: InternalAxiosRequestConfig): Promise<{ data: T }>;
  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig,
  ): Promise<{ data: T }>;
  delete<T = unknown>(url: string, config?: InternalAxiosRequestConfig): Promise<{ data: T }>;
  interceptors: {
    request: {
      use(onFulfilled: (cfg: InternalAxiosRequestConfig) => InternalAxiosRequestConfig): number;
    };
    response: {
      use(
        onFulfilled: (resp: {
          data: unknown;
          status: number;
          config?: InternalAxiosRequestConfig;
        }) => unknown,
        onRejected: (err: AxiosError<ApiErrorEnvelope>) => unknown,
      ): number;
    };
  };
  defaults: { headers: Record<string, unknown> };
}

interface AxiosError<TData = unknown> extends Error {
  response?: { status: number; data: TData; headers: Record<string, string> };
  config?: InternalAxiosRequestConfig;
  request?: unknown;
  code?: string;
  isAxiosError: true;
}

interface InternalAxiosRequestConfig {
  headers?: Record<string, string>;
  url?: string;
  method?: string;
  data?: unknown;
  baseURL?: string;
  timeout?: number;
  params?: object; // GET query string · 接受任何具体 interface (ListXxxRequest 等 · index signature 兼容)
  signal?: AbortSignal; // cancel token · 后续 vertical slice 取消请求用
  // GL-44 · 文档内容流式回传 (openDocumentContent 用 'blob' 拿原始字节 → URL.createObjectURL)。
  // axios runtime 真支持 responseType · 本 ad-hoc 类型补此字段供 blob/arraybuffer 下载场景。
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  // P1-3 · COA 上传进度: axios runtime 真支持 onUploadProgress · ad-hoc 类型补此字段供上传进度回调。
  onUploadProgress?: (event: { loaded: number; total?: number }) => void;
  _skipAuth?: boolean;
  _retried?: boolean;
  // P2-1 · 静默错误：true = 失败不弹全局红 toast (调用方自行降级 · 建议价查询等背景请求用)
  _silentError?: boolean;
  _idemSig?: string; // B3 · in-flight 幂等去重签名 (releaseIdempotencyKey 用)
  _rlRetries?: number; // GL · 429 (网关限流) 已重试次数 · 透明重试上限 RATE_LIMIT_MAX_RETRIES
}

// axios runtime 强制 cast (TS 误判 namespace · runtime 真是 axios function with .create)
const axios = axiosRaw as unknown as { create(config?: Record<string, unknown>): AxiosInstance };

export const TOKEN_KEY = 'oms.auth.access';
export const REFRESH_KEY = 'oms.auth.refresh';
export const USER_KEY = 'oms.auth.user';

// backend JSON error envelope (跟 docs/errors.md · gateway 透传)
export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;
  constructor(code: string, msg: string, status: number, details?: Record<string, unknown>) {
    super(msg);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

// ===== GL · 429 (网关限流) 透明重试 =====
// Dashboard/工作台 load 时并发 ~9-13 GET · 网关 token-bucket 限流在快速刷新下回 429
// (code OMS_GATEWAY_RATE_LIMITED) · 用户会看到一排「网关限流」红 toast。429 是网关在 handler
// 前就拒绝 → 请求从未真正执行 → 全部方法重试安全 (mutating 请求的 Idempotency-Key 也在
// 回放时保留 · attachIdempotencyKey 见到 header 已存在即早返)。这里做防御性透明重试 (后端另
// 行修限流) · 尊重 Retry-After · 退避 + 抖动避免惊群 · 上限兜底防死循环。
const RATE_LIMIT_MAX_RETRIES = 3;
const RATE_LIMIT_DELAY_CAP_MS = 2000; // 退避上限 · 避免 Dashboard 体感卡死
const RATE_LIMIT_JITTER_MS = 250; // 抖动幅度 · 打散并发重试避免惊群 (thundering herd)

// sleep · 返回 setTimeout ms 后 resolve 的 Promise (重试间隔等待用)。
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// rateLimitRetryDelayMs · 计算 429 重试前等待的毫秒数 (纯函数 · 可单测)。
//   - retryAfterHeader: 网关 Retry-After 响应头 (整数秒 · axios 头名小写化 → 'retry-after')。
//     存在且解析为正数 N → base = N 秒 (N * 1000 ms)。
//   - 否则指数退避: 300 * 2 ** attempt ms (attempt = 当前已重试次数 · 0-based)。
//   - base 封顶 RATE_LIMIT_DELAY_CAP_MS (2000ms)。
//   - 叠加 0~250ms 随机抖动 (Math.random) 打散并发重试。
export function rateLimitRetryDelayMs(
  retryAfterHeader: string | undefined,
  attempt: number,
): number {
  let base: number;
  const parsed = retryAfterHeader != null ? Number(retryAfterHeader) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) {
    base = parsed * 1000; // Retry-After 单位为秒
  } else {
    base = 300 * 2 ** attempt; // 指数退避兜底 (无/非法 Retry-After)
  }
  if (base > RATE_LIMIT_DELAY_CAP_MS) base = RATE_LIMIT_DELAY_CAP_MS; // 封顶
  return base + Math.random() * RATE_LIMIT_JITTER_MS; // + 抖动
}

let isRefreshing = false;
let refreshWaiters: Array<(token: string | null) => void> = [];

function waitForRefresh(): Promise<string | null> {
  return new Promise((resolve) => refreshWaiters.push(resolve));
}

function notifyRefreshed(token: string | null) {
  refreshWaiters.forEach((fn) => fn(token));
  refreshWaiters = [];
}

async function refreshToken(http: AxiosInstance): Promise<string | null> {
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return null;
  try {
    const resp = await http.post<{ access_token: string; refresh_token: string }>(
      '/api/v1/identity/auth/refresh',
      { refresh_token: rt },
      { _skipAuth: true } as InternalAxiosRequestConfig & { _skipAuth?: boolean },
    );
    const { access_token, refresh_token } = resp.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
    return access_token;
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

// ===== B3 · Idempotency-Key (in-flight 去重) =====
// 网关幂等中间件 (apps/oms-gateway/internal/middleware/idempotency.go) 按 5 元组 key +
// body sha256 去重：仅当「同一 key 重发」时回放首个响应。故客户端必须让「同一逻辑写操作」
// 复用同一 key —— 否则双击/重试生成不同 key → 网关各自执行 → 重复付款/重复单 (服务端
// 中间件本就启用 · 此前因前端从不发 key 而休眠)。
//
// 策略 = in-flight 去重 (引用计数)：对「方法+URL+body 完全相同且时间上重叠 (尚未结束)」的
// 请求复用同一 key (网关回放首个响应 → 合并为一次)；时间上不重叠的相同请求 (如用户隔一会儿
// 再记一笔同额付款) 拿到不同 key 仍各自执行 —— 避免误吞合法的同额操作。覆盖全部 mutating
// 方法 · 与各表单已有的提交按钮禁用 (confirmLoading/submitting) 构成双层防护。
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const inflightIdem = new Map<string, { key: string; refs: number }>();

function genIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  // 退化 uuid v4 (无 crypto.randomUUID 的环境)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// idempotencySignature · in-flight 去重维度 = 方法 + 完整 URL + 序列化 body。
export function idempotencySignature(cfg: InternalAxiosRequestConfig): string {
  const method = (cfg.method ?? 'GET').toUpperCase();
  const url = (cfg.baseURL ?? '') + (cfg.url ?? '');
  let body = '';
  try {
    body =
      cfg.data == null ? '' : typeof cfg.data === 'string' ? cfg.data : JSON.stringify(cfg.data);
  } catch {
    body = '__nonserializable__'; // 不可序列化 → 不与任何请求合并 (各自唯一)
  }
  return `${method} ${url} ${body}`;
}

// attachIdempotencyKey · 给 mutating 请求挂 in-flight 复用的幂等键 (见上注释)。
export function attachIdempotencyKey(cfg: InternalAxiosRequestConfig): void {
  if (!cfg.headers) return;
  const method = (cfg.method ?? 'GET').toUpperCase();
  if (!MUTATING_METHODS.has(method)) return;
  if (cfg.headers['Idempotency-Key']) return; // 调用方/重放已带 key → 尊重之 (不进 map)
  const sig = idempotencySignature(cfg);
  const existing = inflightIdem.get(sig);
  if (existing) {
    existing.refs += 1;
    cfg.headers['Idempotency-Key'] = existing.key;
  } else {
    const key = genIdempotencyKey();
    inflightIdem.set(sig, { key, refs: 1 });
    cfg.headers['Idempotency-Key'] = key;
  }
  cfg._idemSig = sig;
}

// releaseIdempotencyKey · 请求结束 (成功/失败/超时) 归还引用 · refs 归 0 删 map · 使时间上
// 不重叠的相同请求拿到新 key。delete 戳记保证幂等 (401 refresh 重放不双扣)。
export function releaseIdempotencyKey(cfg?: InternalAxiosRequestConfig): void {
  if (!cfg?._idemSig) return;
  const sig = cfg._idemSig;
  const entry = inflightIdem.get(sig);
  if (entry) {
    entry.refs -= 1;
    if (entry.refs <= 0) inflightIdem.delete(sig);
  }
  delete cfg._idemSig;
}

export function createApiClient(baseURL = '/'): AxiosInstance {
  const http = axios.create({
    baseURL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
  });

  http.interceptors.request.use((cfg) => {
    const skip = (cfg as InternalAxiosRequestConfig & { _skipAuth?: boolean })._skipAuth;
    if (!skip) {
      const tok = localStorage.getItem(TOKEN_KEY);
      if (tok && cfg.headers) cfg.headers.Authorization = `Bearer ${tok}`;
      // B3 · 写操作挂 in-flight 复用的 Idempotency-Key (refresh 等 _skipAuth 请求不挂)
      attachIdempotencyKey(cfg);
    }
    return cfg;
  });

  http.interceptors.response.use(
    (resp) => {
      releaseIdempotencyKey(resp.config); // B3 · 请求结束归还 in-flight 幂等键
      return resp;
    },
    async (err: AxiosError<ApiErrorEnvelope>) => {
      releaseIdempotencyKey(err.config); // B3 · 失败/超时同样归还 (refresh 重放复用 header 上的 key)
      const status = err.response?.status ?? 0;
      const envelope = err.response?.data;
      const code = envelope?.error?.code ?? 'OMS_COMMON_INTERNAL_ERROR';

      // 401 · 尝试 refresh 一次再重放
      if (status === 401 && code !== 'OMS_IDENTITY_AUTH_LOGIN_FAILED') {
        const original = err.config as InternalAxiosRequestConfig & { _retried?: boolean };
        if (original && !original._retried) {
          original._retried = true;
          let newTok: string | null;
          if (isRefreshing) {
            newTok = await waitForRefresh();
          } else {
            isRefreshing = true;
            newTok = await refreshToken(http);
            isRefreshing = false;
            notifyRefreshed(newTok);
          }
          if (newTok) {
            original.headers!.Authorization = `Bearer ${newTok}`;
            return http.request(original);
          }
          // refresh 失败 · 跳 /login
          window.location.href = '/login?next=' + encodeURIComponent(window.location.pathname);
          return Promise.reject(new ApiError(code, '未登录', 401, envelope?.error?.details));
        }
      }

      // 429 · 网关限流透明重试 (尊重 Retry-After · 退避 + 抖动 · 上限兜底)
      // 触发: HTTP 429 或 code === OMS_GATEWAY_RATE_LIMITED。429 在 handler 前被拒 → 请求未执行 →
      // 全部方法重试安全 (回放复用同一 Idempotency-Key)。重试期不 toast · 耗尽才落到下方 toast。
      if (status === 429 || code === 'OMS_GATEWAY_RATE_LIMITED') {
        const original = err.config as InternalAxiosRequestConfig & { _rlRetries?: number };
        if (original) {
          const attempt = original._rlRetries ?? 0; // 当前已重试次数 (0-based)
          if (attempt < RATE_LIMIT_MAX_RETRIES) {
            original._rlRetries = attempt + 1; // ★先自增再回放 · _rlRetries 上限是防死循环唯一守卫
            const retryAfter = err.response?.headers?.['retry-after']; // axios 头名小写化
            await sleep(rateLimitRetryDelayMs(retryAfter, attempt));
            return http.request(original);
          }
          // 重试耗尽 (_rlRetries >= 上限) · 落到下方 i18n/toast + reject (用户最终看到错误)
        }
      }

      // 错误码 → i18n message · 自动 toast
      const i18nKey = `errors:${code}`;
      const text = i18n.exists(i18nKey)
        ? i18n.t(i18nKey)
        : (envelope?.error?.message ?? i18n.t('errors:default'));
      // P2-1 · _silentError: 背景请求 (建议价查询等) 失败静默降级 · 不打扰用户
      const silent = (err.config as InternalAxiosRequestConfig | undefined)?._silentError === true;
      if (status !== 401 && !silent) {
        message.error(text);
      }
      return Promise.reject(new ApiError(code, text, status, envelope?.error?.details));
    },
  );

  return http;
}

export const http = createApiClient('/');
